import { apiFetch } from "../api/client";

/**
 * ═══ CRÉER UN FORMULAIRE VIDE, DEPUIS LE PANNEAU ═══
 *
 * Choisir un formulaire pour une étape supposait qu'il existe déjà, donc un détour par
 * le back office pour en créer un, puis un retour ici pour le choisir (demandé le
 * 2026-09-17). Le panneau sait maintenant en poser un vide, du bon genre — participant
 * ou société — sur CET événement, et le choisir aussitôt ; l'éditeur de champs, juste
 * en dessous, fait le reste.
 *
 * ⚠️ TOUJOURS UN FORMULAIRE DE L'ÉVÉNEMENT, jamais du domaine. Un formulaire du domaine
 * sert tous les événements qui le choisissent : en créer un d'ici, sans le voir, c'est
 * poser une chose partagée sans savoir qu'elle l'est. Celui de l'événement ne touche
 * que lui.
 *
 * ⚠️ MÊME ROUTE QUE LE BACK OFFICE (`POST /domains/:d/events/:e/forms`, corps
 * `{ form, models }`), qui rend l'identifiant du formulaire créé.
 */

/** Un nom qui ne se confond pas avec ceux qui existent déjà sur l'événement. */
export function newFormName(
  existing: ReadonlyArray<string>,
  base = "Nouveau formulaire",
): string {
  const taken = new Set(existing.map((n) => n.trim().toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  for (let i = 2; ; i += 1) {
    const candidate = `${base} ${i}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/** L'identifiant dans la réponse de la route, sous les deux enveloppes possibles. */
export function createdId(answer: unknown): number | undefined {
  if (!answer || typeof answer !== "object") return undefined;
  const shape = answer as { id?: unknown; data?: { id?: unknown } };
  const id = typeof shape.id === "number" ? shape.id : shape.data?.id;
  return typeof id === "number" && Number.isFinite(id) ? id : undefined;
}

export async function createForm(
  domainId: number,
  eventId: number,
  form: { name: string; type: "U" | "C" },
  signal?: AbortSignal,
): Promise<number> {
  const answer = await apiFetch<unknown>(
    `/domains/${domainId}/events/${eventId}/forms`,
    {
      api: "apix",
      method: "POST",
      body: { form: { name: form.name.trim(), type: form.type }, models: [] },
      signal,
    },
  );
  const id = createdId(answer);
  if (id === undefined) {
    throw new Error("Le formulaire a été créé sans rendre son identifiant.");
  }
  return id;
}
